/**
 * ============================================================================
 * CREATE/EDIT LIMIT MODAL v3.1 - BUTTON STRATEGY ALIGNMENT
 * ============================================================================
 *
 * ✅ v3.1: BUTTON STRATEGY ALIGNMENT
 *          - .btn-primary: height:52px, radius:14px (L1 Primary)
 *          - .btn-secondary: height:44px, radius:12px (L2 Secondary)
 *          - .modal-close: 32px + ::before 44px Touch (L4 Mini)
 * ✅ v3.0: Edit Mode Support
 *          - editLimit prop für Bearbeitung bestehender Limits
 *          - Automatisches Befüllen der Felder im Edit-Modus
 *          - Unterschiedlicher Titel: "Neues Limit" vs "Limit bearbeiten"
 * ✅ v2.0: Planivio Branding + Fix Kategorie-Filter
 * ✅ v1.0: Original
 *
 * @version 3.1.0
 */

import React, { useState, useEffect } from 'react';

// Planivio Farben
const PLANIVIO_COLORS = {
  teal: '#4A90A4',
  green: '#6BA893',
  border: '#B8D4DC',
  bgLight: '#E8F4F8',
  bgCard: '#F0F9FF'
};

// Monatsnamen
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

function CreateLimitModal({ show, onClose, categories, onSave, editLimit = null, currentSpending = 0 }) {
  const now = new Date();

  const isEditMode = editLimit !== null;

  const [formData, setFormData] = useState({
    category: '',
    limit_amount: '',
    month: now.getMonth() + 1,
    year: now.getFullYear()
  });

  // Reset when modal opens or editLimit changes
  useEffect(() => {
    if (show) {
      if (editLimit) {
        const existingAmount = editLimit.limit_amount || editLimit.limitAmount || editLimit.amount || '';
        console.log('📝 Edit limit data:', { editLimit, existingAmount });
        setFormData({
          category: editLimit.category || '',
          limit_amount: existingAmount,
          month: editLimit.month || now.getMonth() + 1,
          year: editLimit.year || now.getFullYear()
        });
      } else {
        setFormData({
          category: '',
          limit_amount: '',
          month: now.getMonth() + 1,
          year: now.getFullYear()
        });
      }
    }
  }, [show, editLimit]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      category: formData.category,
      month: parseInt(formData.month),
      year: parseInt(formData.year),
      limit_amount: parseFloat(formData.limit_amount)
    };
    
    console.log('📝 Saving limit:', data);
    onSave(data);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const fixedCategories = categories.filter(c => c.is_fixed === true);
  const variableCategories = categories.filter(c => !c.is_fixed);

  const selectedCat = categories.find(c => c.name === formData.category);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        
        <div className="modal-header">
          <h3>{isEditMode ? '✏️ Limit bearbeiten' : '🎯 Neues Budget-Limit'}</h3>
          <button onClick={onClose} className="modal-close">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            
            {/* Preview wenn Kategorie ausgewählt */}
            {selectedCat && formData.limit_amount && (
              <div className="limit-preview">
                <div
                  className="preview-icon"
                  style={{ background: `${selectedCat.color || PLANIVIO_COLORS.teal}20` }}
                >
                  {selectedCat.icon || '📁'}
                </div>
                <div className="preview-info">
                  <div className="preview-name">{selectedCat.name}</div>
                  <div className="preview-limit">
                    Limit: <strong>{parseFloat(formData.limit_amount).toLocaleString('de-DE')} €</strong>
                  </div>
                  <div className="preview-period">
                    {MONTHS[formData.month - 1]} {formData.year}
                  </div>
                </div>
              </div>
            )}

            {/* Aktueller Verbrauch im Edit-Modus */}
            {isEditMode && formData.limit_amount && (
              <div className="spending-info">
                <div className="spending-header">
                  <span>Aktueller Verbrauch</span>
                  <span className={`spending-percent ${(currentSpending / parseFloat(formData.limit_amount) * 100) > 100 ? 'over' : (currentSpending / parseFloat(formData.limit_amount) * 100) > 80 ? 'warn' : 'ok'}`}>
                    {Math.round(currentSpending / parseFloat(formData.limit_amount) * 100)}%
                  </span>
                </div>
                <div className="spending-bar">
                  <div
                    className={`spending-fill ${(currentSpending / parseFloat(formData.limit_amount) * 100) > 100 ? 'over' : (currentSpending / parseFloat(formData.limit_amount) * 100) > 80 ? 'warn' : 'ok'}`}
                    style={{ width: `${Math.min(currentSpending / parseFloat(formData.limit_amount) * 100, 100)}%` }}
                  />
                </div>
                <div className="spending-amounts">
                  <span>{currentSpending.toLocaleString('de-DE')} € ausgegeben</span>
                  <span>{(parseFloat(formData.limit_amount) - currentSpending).toLocaleString('de-DE')} € übrig</span>
                </div>
              </div>
            )}

            {/* Kategorie */}
            <div className="form-group">
              <label>Kategorie *</label>
              <select 
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                required
              >
                <option value="">Kategorie wählen...</option>
                
                {fixedCategories.length > 0 && (
                  <optgroup label="📌 Fixkosten">
                    {fixedCategories.map(c => (
                      <option key={c.id} value={c.name}>
                        {c.icon || '📁'} {c.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {variableCategories.length > 0 && (
                  <optgroup label="🔄 Variable Kosten">
                    {variableCategories.map(c => (
                      <option key={c.id} value={c.name}>
                        {c.icon || '📁'} {c.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              
              {categories.length === 0 && (
                <div className="form-warning">
                  ⚠️ Keine Kategorien vorhanden. Erstelle zuerst Kategorien.
                </div>
              )}
            </div>

            {/* Limit Betrag */}
            <div className="form-group">
              <label>Budget-Limit *</label>
              <div className="input-with-suffix">
                <input 
                  type="number" 
                  value={formData.limit_amount}
                  onChange={(e) => handleChange('limit_amount', e.target.value)}
                  required 
                  step="0.01"
                  min="0"
                  placeholder="z.B. 500"
                />
                <span className="suffix">€</span>
              </div>
              <div className="form-hint">
                Maximaler Betrag, den du in dieser Kategorie ausgeben möchtest
              </div>
            </div>

            {/* Zeitraum */}
            <div className="form-row">
              <div className="form-group">
                <label>Monat *</label>
                <select
                  value={formData.month}
                  onChange={(e) => handleChange('month', e.target.value)}
                  required
                >
                  {MONTHS.map((name, idx) => (
                    <option key={idx} value={idx + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Jahr *</label>
                <select
                  value={formData.year}
                  onChange={(e) => handleChange('year', e.target.value)}
                  required
                >
                  {[2024, 2025, 2026, 2027].map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Amounts (Picker — nicht Button Strategy) */}
            <div className="form-group">
              <label>Schnellauswahl</label>
              <div className="quick-amounts">
                {[100, 200, 300, 500, 750, 1000].map(amount => (
                  <button
                    key={amount}
                    type="button"
                    className={`quick-amount ${parseFloat(formData.limit_amount) === amount ? 'selected' : ''}`}
                    onClick={() => handleChange('limit_amount', amount.toString())}
                  >
                    {amount} €
                  </button>
                ))}
              </div>
            </div>

          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose}
              className="btn-secondary"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!formData.category || !formData.limit_amount}
            >
              {isEditMode ? '✅ Änderungen speichern' : '💾 Limit speichern'}
            </button>
          </div>
        </form>

      </div>

      {/* ✅ v3.1: Planivio Styles + Button Strategy Alignment */}
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }
        
        .modal {
          background: white;
          border-radius: 20px;
          width: 100%;
          max-width: 440px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .modal-header {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid ${PLANIVIO_COLORS.border};
          background: ${PLANIVIO_COLORS.bgLight};
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 0.95rem;
          font-weight: 600;
          color: #1F2937;
        }

        /* ✅ v3.1: L4 Mini (32px visuell, 44px Touch via ::before) */
        .modal-close {
          width: 32px;
          height: 32px;
          border: none;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6B7280;
          transition: all 0.2s;
          position: relative;
          padding: 0;
          line-height: 1;
          box-sizing: border-box;
        }
        
        /* Touch-Target auf 44px erweitern */
        .modal-close::before {
          content: '';
          position: absolute;
          inset: -6px;
        }
        
        .modal-close:hover {
          background: ${PLANIVIO_COLORS.border};
          color: #1F2937;
        }
        
        .modal-body {
          padding: 1.25rem;
          overflow-y: auto;
          max-height: calc(90vh - 180px);
        }
        
        /* Limit Preview */
        .limit-preview {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          background: ${PLANIVIO_COLORS.bgLight};
          border: 1px solid ${PLANIVIO_COLORS.border};
          border-radius: 10px;
          margin-bottom: 0.75rem;
        }

        /* Spending Info */
        .spending-info {
          padding: 0.75rem;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          margin-bottom: 1rem;
        }

        .spending-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
          font-size: 0.8rem;
          color: #64748B;
        }

        .spending-percent {
          font-weight: 700;
          font-size: 0.85rem;
        }

        .spending-percent.ok { color: #22C55E; }
        .spending-percent.warn { color: #F59E0B; }
        .spending-percent.over { color: #EF4444; }

        .spending-bar {
          height: 6px;
          background: #E2E8F0;
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 0.5rem;
        }

        .spending-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        .spending-fill.ok { background: linear-gradient(90deg, #22C55E, #10B981); }
        .spending-fill.warn { background: linear-gradient(90deg, #F59E0B, #FBBF24); }
        .spending-fill.over { background: linear-gradient(90deg, #EF4444, #F87171); }

        .spending-amounts {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: #64748B;
        }
        
        .preview-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          flex-shrink: 0;
        }
        
        .preview-name {
          font-weight: 700;
          font-size: 1rem;
          color: #1F2937;
        }
        
        .preview-limit {
          font-size: 0.9rem;
          color: #374151;
          margin-top: 0.15rem;
        }
        
        .preview-limit strong {
          color: ${PLANIVIO_COLORS.teal};
        }
        
        .preview-period {
          font-size: 0.8rem;
          color: #6B7280;
          margin-top: 0.15rem;
        }
        
        /* Form Groups */
        .form-group {
          margin-bottom: 1rem;
        }
        
        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #1F2937;
        }
        
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid ${PLANIVIO_COLORS.border};
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.2s;
          box-sizing: border-box;
          background: white;
        }
        
        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: ${PLANIVIO_COLORS.teal};
          box-shadow: 0 0 0 3px rgba(74, 144, 164, 0.15);
        }
        
        .form-group select optgroup {
          font-weight: 600;
          color: #374151;
        }
        
        .form-hint {
          margin-top: 0.5rem;
          font-size: 0.8rem;
          color: #6B7280;
        }
        
        .form-warning {
          margin-top: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: #FEF3C7;
          border: 1px solid #FCD34D;
          border-radius: 8px;
          font-size: 0.8rem;
          color: #92400E;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        
        /* Input with Suffix */
        .input-with-suffix {
          position: relative;
        }
        
        .input-with-suffix input {
          width: 100%;
          padding-right: 2.5rem;
        }
        
        .input-with-suffix .suffix {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6B7280;
          font-weight: 600;
        }
        
        /* Quick Amounts (Picker — nicht Button Strategy) */
        .quick-amounts {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .quick-amount {
          padding: 0.5rem 0.875rem;
          border: 2px solid ${PLANIVIO_COLORS.border};
          background: white;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          color: #374151;
        }
        
        .quick-amount:hover {
          border-color: ${PLANIVIO_COLORS.teal};
          background: ${PLANIVIO_COLORS.bgLight};
        }
        
        .quick-amount.selected {
          background: linear-gradient(135deg, ${PLANIVIO_COLORS.teal} 0%, ${PLANIVIO_COLORS.green} 100%);
          border-color: transparent;
          color: white;
        }
        
        /* ✅ v3.1: Modal Footer mit Button Strategy Alignment */
        .modal-footer {
          padding: 1rem 1.25rem;
          border-top: 1px solid ${PLANIVIO_COLORS.border};
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 0.6rem;
        }

        /* ✅ v3.1: L1 Primary (52px, 14px radius) */
        .btn-primary {
          height: 52px;
          padding: 0 28px;
          background: linear-gradient(135deg, ${PLANIVIO_COLORS.teal} 0%, ${PLANIVIO_COLORS.green} 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 16px rgba(74, 144, 164, 0.25);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          line-height: 1;
          box-sizing: border-box;
          white-space: nowrap;
        }
        
        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(74, 144, 164, 0.4);
        }
        
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        /* ✅ v3.1: L2 Secondary (44px, 12px radius) */
        .btn-secondary {
          height: 44px;
          padding: 0 20px;
          background: white;
          color: #374151;
          border: 2px solid ${PLANIVIO_COLORS.border};
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          line-height: 1;
          box-sizing: border-box;
          white-space: nowrap;
        }
        
        .btn-secondary:hover {
          background: ${PLANIVIO_COLORS.bgLight};
          border-color: ${PLANIVIO_COLORS.teal};
        }
        
        /* Dark Mode */
        .dark .modal {
          background: #1F2937;
        }
        
        .dark .modal-header {
          background: #374151;
          border-color: #4B5563;
        }
        
        .dark .modal-header h3,
        .dark .form-group label,
        .dark .preview-name {
          color: #F9FAFB;
        }
        
        .dark .modal-footer {
          border-color: #4B5563;
        }
        
        .dark .form-group input,
        .dark .form-group select,
        .dark .quick-amount,
        .dark .btn-secondary {
          background: #374151;
          border-color: #4B5563;
          color: #F9FAFB;
        }
        
        .dark .limit-preview {
          background: #374151;
          border-color: #4B5563;
        }
        
        /* Responsive */
        @media (max-width: 480px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          
          .quick-amounts {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default CreateLimitModal;